import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Maximize2 } from "lucide-react";
import { Reveal, ChapterHeader } from "./Reveal";
import { useLang } from "../i18n";
import { useGallery } from "../gallery";

const API = process.env.REACT_APP_BACKEND_URL;

const Lightbox = ({ items, index, onClose, onStep }) => {
  const { t } = useLang();
  const item = items[index];

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onStep(1);
      if (e.key === "ArrowLeft") onStep(-1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    window.__lenis?.stop();
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      window.__lenis?.start();
    };
  }, [onClose, onStep]);

  return (
    <motion.div data-testid="gallery-lightbox" role="dialog" aria-modal="true"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}
      className="fixed inset-0 z-[100] bg-ink/95 backdrop-blur-md flex flex-col" onClick={onClose}>
      <div className="flex items-center justify-between px-4 sm:px-8 h-16 shrink-0">
        <span className="font-mono text-xs tracking-[0.25em] text-slate-500">
          <span className="text-safety">{String(index + 1).padStart(2, "0")}</span> / {String(items.length).padStart(2, "0")}
        </span>
        <button onClick={onClose} data-testid="lightbox-close" aria-label={t("gal_close")}
          className="flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase text-slate-400 hover:text-safety transition-colors">
          {t("gal_close")} <X className="w-5 h-5" />
        </button>
      </div>
      <div className="flex-1 relative flex items-center justify-center px-4 sm:px-20 min-h-0">
        <AnimatePresence mode="wait">
          <motion.img key={item.id} src={`${API}${item.url}`} alt={item.caption || "ZTS F-Invest"}
            data-testid="lightbox-image" onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.02 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="max-h-full max-w-full object-contain shadow-2xl" />
        </AnimatePresence>
        {items.length > 1 && (
          <>
            <button onClick={(e) => { e.stopPropagation(); onStep(-1); }} data-testid="lightbox-prev" aria-label={t("gal_prev")}
              className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center border border-steelborder bg-ink/70 text-slate-300 hover:text-ink hover:bg-safety hover:border-safety transition-colors">
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button onClick={(e) => { e.stopPropagation(); onStep(1); }} data-testid="lightbox-next" aria-label={t("gal_next")}
              className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center border border-steelborder bg-ink/70 text-slate-300 hover:text-ink hover:bg-safety hover:border-safety transition-colors">
              <ChevronRight className="w-6 h-6" />
            </button>
          </>
        )}
      </div>
      <div className="shrink-0 px-4 sm:px-8 py-5 border-t border-steelborder" onClick={(e) => e.stopPropagation()}>
        <p data-testid="lightbox-caption" className="font-body text-sm sm:text-base text-slate-200 max-w-3xl">
          {item.caption || <span className="text-slate-600">—</span>}
        </p>
      </div>
    </motion.div>
  );
};

export const Gallery = () => {
  const { t } = useLang();
  const { items } = useGallery();
  const [active, setActive] = useState(null);

  const close = useCallback(() => setActive(null), []);
  const step = useCallback((d) => setActive((i) => (i + d + items.length) % items.length), [items.length]);

  return (
    <section id="galeria" data-testid="gallery-section" className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-8 max-w-7xl mx-auto min-h-[70vh]">
      <ChapterHeader number="01" eyebrow={t("gal_eyebrow")} title={t("gal_title")} />
      <Reveal className="-mt-6 mb-10 lg:mb-14">
        <p className="text-sm sm:text-base text-slate-400 max-w-2xl">{t("gal_intro")}</p>
      </Reveal>

      {items.length === 0 && (
        <Reveal>
          <div data-testid="gallery-empty" className="border border-dashed border-steelborder bg-steelcard/50 px-6 py-16 text-center">
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-slate-500">{t("gal_empty")}</p>
          </div>
        </Reveal>
      )}

      <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 lg:gap-6 [column-fill:_balance]">
        {items.map((p, i) => (
          <Reveal key={p.id} delay={(i % 3) * 0.08} className="mb-4 lg:mb-6 break-inside-avoid">
            <button onClick={() => setActive(i)} data-testid={`gallery-item-${i}`}
              className="group relative block w-full text-left border border-steelborder bg-steelcard overflow-hidden focus:outline-none focus-visible:border-safety">
              <div className="relative overflow-hidden">
                <img src={`${API}${p.thumb_url}`} alt={p.caption || "ZTS F-Invest"} loading="lazy"
                  width={p.width} height={p.height}
                  className="w-full h-auto block transition-transform duration-700 ease-out group-hover:scale-[1.04] grayscale-[25%] group-hover:grayscale-0" />
                <span className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent opacity-70 group-hover:opacity-40 transition-opacity duration-500" />
                <span className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center bg-ink/70 border border-steelborder text-slate-300 opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
                  <Maximize2 className="w-4 h-4" />
                </span>
                <span className="absolute bottom-3 left-3 font-mono text-[10px] tracking-[0.25em] text-safety">
                  {String(i + 1).padStart(2, "0")}
                </span>
              </div>
              {p.caption && (
                <p data-testid={`gallery-caption-${i}`} className="px-4 py-3 text-sm text-slate-300 border-t border-steelborder group-hover:text-slate-100 transition-colors">
                  {p.caption}
                </p>
              )}
            </button>
          </Reveal>
        ))}
      </div>

      <AnimatePresence>
        {active !== null && <Lightbox items={items} index={active} onClose={close} onStep={step} />}
      </AnimatePresence>
    </section>
  );
};
